import { Card, Progress, Row, Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import { CardSkeleton } from './CardSkeleton';
import { SHARED_CARD_STYLES } from '../constants';
import { SharedCardHeader } from './SharedCardHeader';
import { StatusCardColumnConfigItem } from '../types';

type StatusCardProps = {
  title: string;
  tooltipText: string;
  progressPercent: number;
  progressValue: number | string;
  progressLabel: string;
  tableData: any[]; // Data for the table rows
  tableColumnsConfig: StatusCardColumnConfigItem[]; // Configuration for table columns
  tableMaxHeight?: string; // New prop for scrollable area max height (e.g., '200px')
  showViewButton?: boolean;
  onViewClick?: () => void;
  linkTo?: string;
  disabled?: boolean;
  progressSize?: number;
  progressStrokeWidth?: number;
  progressColor?: string;
  showComingSoon?: boolean;
};

export const StatusCard = ({
  title,
  tooltipText,
  progressPercent,
  progressValue,
  progressLabel,
  tableData,
  tableColumnsConfig,
  tableMaxHeight,
  showViewButton = true,
  onViewClick,
  linkTo,
  disabled = false,
  progressSize = 165,
  progressStrokeWidth = 8,
  progressColor = '#2D5BFF',
  showComingSoon,
}: StatusCardProps) => {
  const navigate = useNavigate();

  const handleViewClick = () => {
    if (onViewClick) {
      onViewClick();
    } else if (linkTo) {
      navigate(linkTo);
    }
  };

  const shouldShowViewButton = Boolean(
    showViewButton && (onViewClick || linkTo) && !disabled,
  );

  return (
    <Card
      className={SHARED_CARD_STYLES.className}
      headStyle={SHARED_CARD_STYLES.headStyle}
      bodyStyle={SHARED_CARD_STYLES.bodyStyle}
      title={
        <SharedCardHeader
          title={title}
          tooltipText={tooltipText}
          disabled={disabled}
          shouldShowViewButton={shouldShowViewButton}
          onViewClick={handleViewClick}
          showComingSoon={showComingSoon}
        />
      }
      bordered={false}
    >
      {disabled ? (
        <CardSkeleton />
      ) : (
        <div className="flex items-start justify-between">
          <div className="flex flex-col items-center justify-center">
            <Progress
              type="circle"
              percent={progressPercent}
              strokeWidth={progressStrokeWidth}
              size={progressSize}
              strokeColor={progressColor}
              format={() => (
                <span className="text-4xl font-bold">{progressValue}</span>
              )}
            />
            <div className="mt-2 text-base font-normal text-gray-500 text-center">
              {progressLabel}
            </div>
          </div>
          <div className="w-full ml-4 flex flex-col">
            {/* Table Headers at the top of the table area */}
            <Row className="px-4 text-sm font-medium mb-2 pb-2 border-b border-gray-200">
              {tableColumnsConfig.map((col) => (
                <Col
                  key={col.key}
                  span={col.span}
                  className={`px-2 text-${col.align || 'left'} text-gray-600`}
                >
                  {col.title}
                </Col>
              ))}
            </Row>
            {/* Data Rows wrapped in a scrollable div */}
            <div
              style={{
                maxHeight: tableMaxHeight || 'none',
                overflowY: tableMaxHeight ? 'auto' : 'visible',
              }}
            >
              {tableData.map((item, rowIndex) => (
                <Row
                  key={item.key || item.id || rowIndex}
                  align="middle"
                  className={`text-sm px-4 py-2 ${
                    rowIndex === tableData.length - 1
                      ? ''
                      : 'border-b border-gray-200'
                  }`}
                >
                  {tableColumnsConfig.map((col) => (
                    <Col
                      key={col.key}
                      span={col.span}
                      className={`px-2 text-${col.align || 'left'}`}
                    >
                      {col.render
                        ? col.render(
                            col.dataIndex
                              ? item[col.dataIndex as keyof typeof item]
                              : undefined,
                            item,
                            rowIndex,
                          )
                        : col.dataIndex
                        ? item[col.dataIndex as keyof typeof item]
                        : null}
                    </Col>
                  ))}
                </Row>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
